import { cn } from "@/lib/utils";
import Tooltip from "@/components/ui/Tooltip";

type BadgeVariant =
  "default" | "accent" | "success" | "warning" | "error" | "info";

const variantClasses: Record<BadgeVariant, string> = {
  // Neutral pill on brand tokens (BRANDING.md § 2.2 Divider Soft / § 2.1
  // Dark Gray) — visually near-identical to the prior gray-100/gray-700
  // pair, now token-backed.
  //
  // Status variants moved off the Tailwind ramps onto the semantic state ramp
  // on 2026-08-26 (PO direction; docs/product/experience-and-ui.md semantic
  // state colors, defined in
  // docs/engineering/contracts/UI_COMPONENT_CONTRACT.md § Token surface).
  // The raw semantic hexes (#4CAF82 / #D4932A / #E05A5A) are FILL values and
  // fail WCAG AA as text on a light surface (2.71 / 2.62 / 3.63 : 1), so each
  // pill pairs a pale surface with a darkened label stop — the same split the
  // brand accent already uses via --color-primary-text. Measured AA ratios sit
  // beside each token in styles/index.css (5.02 / 5.01 / 5.00 : 1).
  //
  // `info` deliberately stays on the blue ramp: UI_COMPONENT_CONTRACT
  // § Token surface reserves raw blues for exactly this variant.
  //
  // NON-STATUS LANE (`default`, `accent`) — added 2026-08-26 by
  // ENG-STATUS-COLOUR-MISUSE-SWEEP. experience-and-ui.md:3221 forbids using
  // the semantic state colours outside their status semantics, and once the
  // status variants moved onto that ramp every category tag, plan tier, and
  // debug flag wearing `success`/`warning` began asserting a state it does
  // not have. Those values route here instead. `accent` is the brand-anchored
  // half of the pair (so a two-value non-status set like WIRED/STUB stays
  // visually distinguishable without either side reading as good or bad); it
  // reuses the existing --color-primary-soft / --color-primary-text split,
  // measured 4.65:1 on a light surface — AA for normal text.
  default: "bg-divider-soft text-ink-soft",
  accent: "bg-primary-soft text-primary-text",
  success: "bg-success-bg text-success-text",
  warning: "bg-warning-bg text-warning-text",
  error: "bg-error-bg text-error-text",
  info: "bg-blue-50 text-blue-700",
};

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  className?: string;
  /**
   * A definition behind the badge (e.g., F6 Starter cap indicator,
   * RuleTypeBadge): rendered through the Tooltip primitive on a focusable
   * span — opens on hover OR focus, closes on Esc (in-app design spec § 12
   * § 2.3; migration wave 3 retired the native `title` pass-through).
   */
  tooltip?: React.ReactNode;
  /** @deprecated the pre-wave-3 name of `tooltip`; still rendered through the
   * Tooltip primitive (never a native title) so a caller a parallel branch
   * still carries keeps compiling. */
  title?: string;
}

export default function Badge({
  children,
  variant = "default",
  className,
  tooltip,
  title,
}: BadgeProps) {
  const definition = tooltip ?? title;
  const badgeClass = cn(
    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
    variantClasses[variant],
    className,
  );
  if (definition) {
    return (
      <Tooltip content={definition}>
        <span tabIndex={0} className={cn(badgeClass, "min-h-6")}>
          {children}
        </span>
      </Tooltip>
    );
  }
  return <span className={badgeClass}>{children}</span>;
}
